import type { ClassSessionRecord } from '../api/endpoints/sessions';
import type { BusinessDetail } from '../api/schemas';
import { dayHours, layOutTimed, minuteOfDay, UNASSIGNED_COLUMN, type TimedBlock } from './calendarDay';
import { isSameDay } from './format';

export type ClassBlock = TimedBlock<ClassSessionRecord>;
export type ClassColumn = { id: string; name: string; blocks: ClassBlock[] };
export type ClassDay = { startHour: number; endHour: number; columns: ClassColumn[]; count: number };

type TimedSession = { item: ClassSessionRecord; start: number; end: number };

/**
 * A studio's day on the same paper-book grid as the barbers': a column per
 * coach, each class one block however many people booked it (a class of
 * twelve is one thing to teach, not twelve slivers). Cancelled classes stay off.
 */
export function buildClassDay(
  sessions: ClassSessionRecord[],
  staff: BusinessDetail['staff'],
  day: Date,
  onlyStaffId?: string,
): ClassDay {
  const timed: TimedSession[] = sessions
    .filter((session) => session.status === 'published' && isSameDay(new Date(session.startsAt), day))
    .map((session) => ({ item: session, start: minuteOfDay(session.startsAt), end: endMinute(session) }));
  const { startHour, endHour } = dayHours(timed);
  const offset = startHour * 60;
  const roster = staff.filter((member) => !onlyStaffId || member.id === onlyStaffId);
  const rosterIds = new Set(roster.map((member) => member.id));
  const columns: ClassColumn[] = roster.map((member) => ({
    id: member.id,
    name: member.name,
    blocks: layOutTimed(timed.filter((entry) => entry.item.instructorId === member.id), offset),
  }));
  const unassigned = timed.filter((entry) => !rosterIds.has(entry.item.instructorId));
  if (!onlyStaffId && unassigned.length > 0) {
    columns.push({ id: UNASSIGNED_COLUMN, name: 'Sin asignar', blocks: layOutTimed(unassigned, offset) });
  }
  return { startHour, endHour, columns, count: columns.reduce((sum, column) => sum + column.blocks.length, 0) };
}

/** Seats already taken in a class; a stale availability never reads as negative. */
export function seatsTaken(session: ClassSessionRecord): number {
  return Math.max(session.capacity - session.availableCapacity, 0);
}

/** A class that runs past midnight still ends on the grid it started on. */
function endMinute(session: ClassSessionRecord): number {
  const start = new Date(session.startsAt);
  const minutes = Math.round((new Date(session.endsAt).getTime() - start.getTime()) / 60_000);
  return minuteOfDay(session.startsAt) + Math.max(minutes, 0);
}
